import {
  Box,
  Checkbox,
  Flex,
  FormControl,
  Switch,
  Tooltip,
} from '@chakra-ui/react'
import { InsuranceParamsPropertyDto } from 'BrokerTsLib/src/dtos'
import { Insurer, Risk, RiskVariant, Selection } from 'BrokerTsLib/src/enums'
import { useContext, useEffect } from 'react'
import { useFormContext } from 'react-hook-form'

import { FormLabel } from '../../../components/FormLabel'
import { Loading } from '../../../components/Loading'
import { SwitchControl } from '../../../components/SwitchControl'
import { useGetInsurersSpecificParams } from '../../../hooks/useInsuranceParams'
import { LabelHintIcon } from '../../../icons'
import { insuranceParamsContext } from '../../../layouts/Wizard'
import { BaScoringCheckbox } from '../BAScoringCheckbox'
import { IndividualCompany } from '../IndividualCompany'
import {
  IndividualDiscountsGroup,
  IndividualDiscountsWrapper,
  LocalInputGroup,
} from '../IndividualDiscountsWrapper'

export const IndividualDiscounts = () => {
  const { insuranceParamsId: paramsId, insuranceParams: ip } = useContext(
    insuranceParamsContext,
  )!
  const insuranceParams = ip as InsuranceParamsPropertyDto
  const isContracted = !!insuranceParams?.generalParams.contractNumber

  const { setValue, getValues, register, watch } = useFormContext()

  const { refetch, data, isLoading, isFetching } = useGetInsurersSpecificParams(
    paramsId!,
    null,
    {
      enabled: false,
      onSuccess: ({ data }) => {
        setValue('individualDiscounts', data)
      },
    },
  )

  const uniqaIndex = data?.data?.findIndex((x) => x.insurer == Insurer.Uniqa)
  const csobIndex = data?.data?.findIndex((x) => x.insurer == Insurer.Csob) ?? -1
  const cppIndex = data?.data?.findIndex((x) => x.insurer == Insurer.CPP) ?? -1
  const koopIndex = data?.data?.findIndex((x) => x.insurer == Insurer.Kooperativa) ?? -1

  useEffect(() => {
    if (!paramsId) return

    refetch()
  }, [paramsId])

  const loading = isLoading || isFetching

  const checkedPillow = insuranceParams?.property.insurers.find(
    (x) => x == Insurer.Pillow,
  )

  // platne pro vybrane pojistovny, byt a stavbu (stavba je bud ve vychozi varinte, nebo nektere dalsi variante)
  const showNewPrice = (csobIndex > -1 || cppIndex > -1 || koopIndex > -1) &&
    (insuranceParams?.property.data.realtyTypeId ?? Selection.RodinnyDum) == Selection.Byt && (
      ((insuranceParams?.property.type ?? RiskVariant.Household) == RiskVariant.HouseholdAndRealty ||
        (insuranceParams?.property.type ?? RiskVariant.Household) == RiskVariant.Realty) ||
      (insuranceParams?.property.variants?.findIndex(v =>
        (v.variantParts?.findIndex(vp1 => vp1.risk == Risk.PROPERTY_TYPE &&
          (vp1.riskVariant == RiskVariant.HouseholdAndRealty || vp1.riskVariant == RiskVariant.Realty)) ?? -1) > -1
      ) ?? -1) > -1
    )

  return (
    <IndividualDiscountsWrapper>
      {loading && <Loading large={true} />}

      <IndividualDiscountsGroup>
        <FormControl>
          <FormLabel tooltip="Klient souhlasí s valorizací / indexací pojistné částky domácnosti či stavby pojišťovnou.">
            Souhlas s valorizací / indexací pojistné částky
          </FormLabel>

          <SwitchControl>
            <Switch
              {...register('property.data.propertyValorisationAgreement')}
              isChecked={watch('property.data.propertyValorisationAgreement')}
              isDisabled={isContracted}
            />
          </SwitchControl>
        </FormControl>

        {showNewPrice &&
          <FormControl>
            <FormLabel tooltip="Zašktněte, pokud klient preferuje pojištění bytové jednotky v nové ceně. Tuto možnost nabízí pouze pojišťovny ČPP, Kooperativa a ČSOB, ostatní poskytují plnění podle všeobecných pojistných podmínek.">
              Pojištění bytové jednotky na novou cenu
            </FormLabel>

            <SwitchControl>
              <Switch
                {...register('property.data.insuranceInNewPrice')}
                isChecked={watch('property.data.insuranceInNewPrice')}
                isDisabled={isContracted}
              />
            </SwitchControl>
          </FormControl>
        }

        {!loading && (data?.data ?? []).length > 0 && (
          <>
            <IndividualCompany id={Insurer.Csob} name="ČSOB Pojišťovna">
              <FormControl>
                <BaScoringCheckbox
                  isContracted={isContracted}
                  name={`individualDiscounts.${csobIndex}.baScoring`}
                />
              </FormControl>

              {/* TODO - odstranit po produkčním spuštění WS ZEUS - tato WS nepožaduje číslo pojistné smlouvy - zjistí si sama */}
              <LocalInputGroup
                id="contract-other-csob"
                name="contract-other-csob"
                label="Pojistná smlouva u ČSOB Pojištovna"
                tooltip="Zadejte číslo aktivní smlouvy klienta v pojišťovně ČSOB. Pojišťovna může poskytnout dodatečnou slevu na pojistném."
                optional
                isDisabled={isContracted}
                placeholder="Číslo pojistné smlouvy"
                defaultValue={
                  getValues(
                    `individualDiscounts.${csobIndex}.contractNumber`,
                  ) ?? ''
                }
                onChange={(e: any) =>
                  setValue(
                    `individualDiscounts.${csobIndex}.contractNumber`,
                    e.target.value,
                  )
                }
              />
            </IndividualCompany>

            <IndividualCompany id={Insurer.Uniqa} name="UNIQA">
              <LocalInputGroup
                id="contract-other-uniqa"
                name="contract-other-uniqa"
                label="Pojistná smlouva u Uniqa"
                tooltip="Zadejte číslo aktivní smlouvy klienta v pojišťovně UNIQA.  Pojišťovna může poskytnout dodatečnou slevu na pojistném."
                optional
                isDisabled={isContracted}
                placeholder="Číslo pojistné smlouvy"
                defaultValue={
                  getValues(
                    `individualDiscounts.${uniqaIndex}.contractNumber`,
                  ) ?? ''
                }
                onChange={(e: any) =>
                  setValue(
                    `individualDiscounts.${uniqaIndex}.contractNumber`,
                    e.target.value,
                  )
                }
              />
            </IndividualCompany>
          </>
        )}

        {checkedPillow && (
          <IndividualCompany id={Insurer.Pillow} name="Pillow">
            <FormControl>
              <Checkbox
                defaultChecked={getValues(`property.data.securityFireAlarm`)}
                value="1"
                isDisabled={isContracted}
                onChange={(e) =>
                  setValue(`property.data.securityFireAlarm`, e.target.checked)
                }
              >
                <Flex gap={2}>
                  Požární hlásič
                  <Tooltip
                    hasArrow
                    label="Požární hlásič je kouřový nebo teplotní detektor v centrální části bytu nebo v každém patře rodinného domu. Musí mít akustickou signalizaci a také informovat minimálně dva mobilní telefony."
                    placement="auto"
                  >
                    <Box sx={{ lineHeight: 0 }}>
                      <LabelHintIcon width={4} height={4} />
                    </Box>
                  </Tooltip>
                </Flex>
              </Checkbox>
            </FormControl>

            <FormControl>
              <Checkbox
                defaultChecked={getValues(`property.data.securityWaterLeakage`)}
                value="1"
                isDisabled={isContracted}
                onChange={(e) =>
                  setValue(
                    `property.data.securityWaterLeakage`,
                    e.target.checked,
                  )
                }
              >
                <Flex gap={2}>
                  Detektor úniku vody
                  <Tooltip
                    hasArrow
                    label="Detektor úniku vody umístěný např. pod pračkou, myčkou atd. Musí mít akustickou signalizaci a také informovat minimálně dva mobilní telefony."
                    placement="auto"
                  >
                    <Box sx={{ lineHeight: 0 }}>
                      <LabelHintIcon width={4} height={4} />
                    </Box>
                  </Tooltip>
                </Flex>
              </Checkbox>
            </FormControl>

            <FormControl>
              <Checkbox
                defaultChecked={getValues(`property.data.securityBurglarAlarm`)}
                value="1"
                isDisabled={isContracted}
                onChange={(e) =>
                  setValue(
                    `property.data.securityBurglarAlarm`,
                    e.target.checked,
                  )
                }
              >
                <Flex gap={2}>
                  Alarm vloupání
                  <Tooltip
                    hasArrow
                    label="Detektor pohybu v centrální části bytu nebo detektor otevření všech vstupních dveří se signalizací na min. dva mobilní telefony. Telefony musí být funkční, zapnuté a nacházet se ve vašem dosahu. Alarm musí být v době vloupání ve stavu střežení a musí mít vlastní záložní zdroj. V případě pojistné události musíte doložit záznamy signalizace."
                    placement="auto"
                  >
                    <Box sx={{ lineHeight: 0 }}>
                      <LabelHintIcon width={4} height={4} />
                    </Box>
                  </Tooltip>
                </Flex>
              </Checkbox>
            </FormControl>

            <FormControl>
              <Checkbox
                defaultChecked={getValues(
                  `property.data.securityCentralSecurityDesk`,
                )}
                value="1"
                isDisabled={isContracted}
                onChange={(e) =>
                  setValue(
                    `property.data.securityCentralSecurityDesk`,
                    e.target.checked,
                  )
                }
              >
                <Flex gap={2}>
                  Pult centralizované ochrany
                  <Tooltip
                    hasArrow
                    label="Pult centralizované ochrany bezpečnostní služby nebo Policie ČR musí být v době vloupání aktivní, zapojen do elektrické sítě vlastním jističem a mít záložní zdroj. Tento poplachový zabezpečovací a tísňový systém musí být instalován firmou s oprávněním k této činnosti a v případě pojistné události musíte doložit zápis o výchozí i periodické revizi alarmu."
                    placement="auto"
                  >
                    <Box sx={{ lineHeight: 0 }}>
                      <LabelHintIcon width={4} height={4} />
                    </Box>
                  </Tooltip>
                </Flex>
              </Checkbox>
            </FormControl>
          </IndividualCompany>
        )}
      </IndividualDiscountsGroup>
    </IndividualDiscountsWrapper>
  )
}
